import type { PerspectiveCamera, Scene, WebGLRenderer } from "three";
import type { PoseInterpolator } from "./poseInterpolator";
import type { SkeletonGroups } from "../scene/skeletonGroup";
import type { VideoClock } from "./videoClock";

/**
 * Single rAF loop. Reads the master clock, finds the matching pose frame,
 * pushes joints into the skeleton groups, then renders.
 *
 * The caller controls which camera the renderer uses (free-look orbit cam
 * vs snap-to-cam) by mutating `state.activeCamera` between ticks.
 */
export interface RafState {
  activeCamera: PerspectiveCamera;
  onTick?: (alignedT: number) => void;  // for UI to read time without a 2nd raf
}

export function startRafLoop(
  renderer: WebGLRenderer,
  scene: Scene,
  state: RafState,
  clock: VideoClock,
  poses: PoseInterpolator,
  skeletons: SkeletonGroups,
): () => void {
  let running = true;
  function tick(): void {
    if (!running) return;
    // Read activeCamera off `state` each tick — callers swap it on mode change.
    const t = clock.currentTime - poses.start;
    const frame = poses.frameAt(t);
    skeletons.applyFrame(frame);
    state.onTick?.(t);
    renderer.render(scene, state.activeCamera);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
  return () => { running = false; };
}
